import type { TokenDefinition, } from "@bracket/chainography";
import { getTheme, themed, Stream, Box, Dropdown, Input, Text as Tx, } from "@bracket/ui";
import { useSwap, } from "@store";
import type { FunctionComponent, } from "react";
import styled from "styled-components";



export declare type TokenAmountOutputProps = {
	disabled?: boolean;
};



/**
 * A component that displays the output token amount and allows token selection in a swap interface.
 * Shows a dropdown for token selection and an input field for the amount.
 * The component is used in both buy and redeem modes of the swap interface.
 *
 * @component
 * @param {TokenAmountOutputProps} props - The component props
 * @param {boolean} [props.disabled=false] - Whether the component is disabled
 * @returns {JSX.Element} A container with token dropdown and amount input
 */
export const TokenAmountOutput: FunctionComponent<TokenAmountOutputProps> = ({
	disabled = false,
}) => {

	const {
		brokeTokens,
		tokenOut,
		tokensOut,
		amountOut,
		onTokenOutChange,
		onTokenOutAmountChange,
	} = useSwap();

	return (
		<InputContainer>
		{
			tokensOut.length ? (
				<Dropdown<TokenDefinition>
					items={tokensOut}
					selectedItem={tokenOut}
					onSelect={onTokenOutChange}
					disabledItems={brokeTokens}
					disabled={disabled}
					renderItem={(token, disabled) => {
						return (
							<ListItem $disabled={disabled}>
								<ImageBrace $url={`url(${token.logoURI})`} />
								<Tx md>{token.symbol}</Tx>
							</ListItem>
						);
					}}
					renderSelectedItem={token => {
						if(token) {
							return (
								<>
									<ImageBrace $url={`url(${token.logoURI})`} />
									<Tx md>{token.symbol}</Tx>
								</>
							)
						}
						return (
							<Tx md>Select a token</Tx>
						);
					}}
				/>
			) : null}
			<StyledInput
				value={amountOut}
				onChange={evt => !disabled && onTokenOutAmountChange(evt)}
				disabled={disabled}
				type="text"
			/>
		</InputContainer>
	);
};

const ImageBrace = styled(Box)<{ $url: string; }>`
	width: 24px;
	height: 24px;
	margin-right: 8px;
	background-image: ${props => props.$url};
	background-size: contain;
	align-items: center;
	justify-content: center;
	background-repeat: no-repeat;
`;

const InputContainer = styled(Box)`
	background-color: ${() => getTheme().surface_container};
	border-radius: ${() => getTheme().radials.sm};
	overflow: hidden;
	height: 88px;
	align-items: center;
`;

const StyledInput = styled(Input)`
	margin-left: -350px;
	/* Ensure decimal separator is always a period */
	&::-webkit-inner-spin-button,
	&::-webkit-outer-spin-button {
		-webkit-appearance: none;
		margin: 0;
	}
`;

const ListItem = styled(Stream)<{ $disabled?: boolean; }>`
	pointer-events: ${props => props.$disabled ? "none" : "auto"};
`;
